import { CommonModule } from '@angular/common';
import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbScrollSpyFragment,
  NgbDropdownModule,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { Component, computed, inject, Injector, OnInit, signal, ViewChild } from '@angular/core';
import {
  BaseDocLineDto,
  CreateUpdateCustomerReturnNoteDto,
  CustomerCreditNoteDto,
  CustomerCreditNoteService,
  CustomerDocDefaultValueDto,
  CustomerReturnNoteDto,
  CustomerReturnNoteService,
  DayBookDto,
  DayBookService,
} from '@digiwall/goliath-documents/proxy';
import { ClassificationTypeService, DocRole } from '@digiwall/goliath-core/proxy';
import { ReportViewerComponent } from '@odyssey/reporting';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import {
  CustomerService,
  PointOfSaleService,
  ThirdPartyDto,
} from '@digiwall/goliath-contacts/proxy';
import { DocTemplateService } from '../../base-docs/detail/doc-template-selection/doc-template.service';
import { GoliathContactsEnumerationTypeStore, SalesPersonStore } from '@digiwall/goliath-contacts';
import { concatMap, Observable, of } from 'rxjs';
import { AnnexesListComponent, GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import { CustomerReturnNoteLinesEditorComponent } from './customer-return-note-lines/customer-return-note-lines-editor.component';
import {
  ChatComponent,
  DeactivatableComponentService,
  HistoryComponent,
  OdysseyOffcanvas,
} from '@digiwall/core';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { CustomerCreditNoteCreationDialogComponent } from './customer-credit-note-creation-dialog/customer-credit-note-creation-dialog.component';
import { BaseCustomerDocDetail } from '../../base-customer-docs/base-customer-doc-detail';
import { FieldMapping } from '../../base-third-party-docs/base-third-party-doc-detail';
import { DocReportPermission } from '@digiwall/goliath-documents/config';
import { CounterComponent, DropdownMenuComponent, PanelComponent } from '@digiwall/ui-kit';
import { DwGridDetailNavigationComponent, DwTabsComponent } from '@digiwall/ag-grid';
import { DwNavLinkDirective } from '@digiwall/core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DwKeyboardShortcutDirective } from '@digiwall/core';
import { DwScrollSpyDirective } from '@digiwall/ag-grid';
import { DocConditionsComponent } from '../../base-docs/detail/doc-conditions/doc-conditions.component';
import { DocClassificationsComponent } from '../../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocCustomerAddressesComponent } from '../../base-docs/detail/doc-customer-addresses/doc-customer-addresses.component';
import { DocCustomerOthersComponent } from '../../base-docs/detail/doc-customer-others/doc-customer-others.component';
import { AsyncPipe } from '@angular/common';
import { SidePanelComponent } from '@digiwall/ui-kit';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { DocSidePanelComponent } from '../../base-docs/detail/doc-ui-components/doc-side-panel/doc-side-panel.component';
import {
  DocNumberTagComponent,
  DocumentCoreCardComponent,
  DocumentCustomerCardComponent,
  DocumentCustomerDateCardComponent,
  DocumentCustomerStatusCardComponent,
} from '../../../components';
import { StockValidationSharedLogicService } from '../../stock-validation-shared-logics/stock-validation-shared-logic.service';
import { CustomerInvoicePaymentsComponent } from '../../customer-invoices/customer-invoice-detail/customer-invoice-payments/customer-invoice-payments.component';

@Component({
  templateUrl: './customer-return-note-detail.component.html',
  styleUrl: './customer-return-note-detail.component.scss',
  providers: [DocTemplateService, StockValidationSharedLogicService],
  imports: [
    CommonModule,
    PanelComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    DwNavLinkDirective,
    NgbNavContent,
    NgbScrollSpy,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    DwKeyboardShortcutDirective,
    NgbScrollSpyFragment,
    DwScrollSpyDirective,
    CustomerReturnNoteLinesEditorComponent,
    DocConditionsComponent,
    DocClassificationsComponent,
    DocCustomerAddressesComponent,
    DocCustomerOthersComponent,
    AnnexesListComponent,
    NgbDropdownModule,
    DropdownMenuComponent,
    NgbNavOutlet,
    SidePanelComponent,
    FormsModule,
    DialogBoxComponent,
    ReportViewerComponent,
    AsyncPipe,
    LocalizationModule,
    CounterComponent,
    ChatComponent,
    DwGridDetailNavigationComponent,
    DocSidePanelComponent,
    DocumentCoreCardComponent,
    DocumentCustomerCardComponent,
    DocumentCustomerDateCardComponent,
    DocumentCustomerStatusCardComponent,
    HistoryComponent,
    DocNumberTagComponent,
  ],
})
export class CustomerReturnNoteDetailComponent
  extends BaseCustomerDocDetail<
    CustomerReturnNoteService,
    CustomerReturnNoteDto,
    CreateUpdateCustomerReturnNoteDto,
    CustomerDocDefaultValueDto
  >
  implements OnInit
{
  @ViewChild('customerReturnNoteLineGrid') docLineGrid: CustomerReturnNoteLinesEditorComponent;
  @ViewChild('report') reportViewer: ReportViewerComponent;

  docRole = DocRole.CustomerReturnNote;

  dayBook: DayBookDto;
  canAddPayment = signal(false);
  remainingAmount$: Observable<number>;

  enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  contactEnumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  salesPersonStore = inject(SalesPersonStore);
  stockValidationSharedLogicService = inject(StockValidationSharedLogicService);

  private validateInProgress: boolean = false;

  protected saveActionBtns = computed(() => {
    let btns = [
      {
        label: this.localizationService.instant('::Save'),
        action: () => this.save(),
        disabled: () =>
          this.isValidated() &&
          (!this.form.dirty || !this.stockValidationSharedLogicService.canSave()),
      },
    ];
    return btns;
  });

  actions = computed(() => ({
    groups: [
      this.baseActionsThirdPartyDoc().concat(
        {
          label: '::GeneratePdf',
          icon: 'digi-out-pdf-file',
          action: () => this.generatePdf(this.reportViewer),
        },
        {
          label: 'Doc::CustomerInvoice:AddPayment',
          icon: 'digi-out-payment-method',
          visible: computed(() => {
            return this.canAddPayment();
          }),

          action: () => this.addPayment(),
        },
        {
          label: 'Doc::PrintDocument',
          icon: 'digi-out-print',
          action: () => this.printDocument(),
          enabled: this.canPrintDocument,
        },
        {
          label: 'Doc::CustomerReturnNote:ToCustomerCreditNote',
          icon: 'digi-out-add-file',
          action: () => this.toCustomerCreditNote(),
          visible: signal<boolean>(!this.entity.isClosed),
        },
      ),
      [
        {
          label: 'Doc::GenerateDocPrintTemplate',
          icon: 'digi-out-edit-square',
          action: () => this.openDesigner(),
          requiredPolicy: DocReportPermission.Create,
        },
      ],
    ],
  }));

  totalAmount: number = 0;
  totalAmountVatIncluded: number = 0;

  override get defaultValue() {
    return Object.assign(this.baseDefaultValue, {
      isExported: false,
      allowPartialDelivery: false,
    });
  }

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    private customerReturnNoteService: CustomerReturnNoteService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    injector: Injector,
    customerService: CustomerService,
    config: ConfigStateService,
    docTemplateService: DocTemplateService,
    classificationTypeService: ClassificationTypeService,
    protected pointOfSaleService: PointOfSaleService,
    confirmationService: ConfirmationService,
    private customerCreditNoteService: CustomerCreditNoteService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      route,
      router,
      subscriptionService,
      customerReturnNoteService,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      docTemplateService,
      injector,
      customerService,
      config,
      confirmationService,
      deactivableComponentService,
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.stockValidationSharedLogicService.setup({
      isCreationMode: this.isCreationMode,
      isValidated: this.isValidated,
      lineCount: this.lineCount,
      isStockValidated: this.isStockValidated,
    });
    this.init();
  }

  override get formModel() {
    return Object.assign({}, super.formModel, {
      invoicingAddress_ThirdPartyAddressId: [null],
      returnReceivedDate: [null],
    });
  }

  toCustomerCreditNote() {
    if (this.form.get('isBlocked').value) {
      this.isBlockedError();
      return;
    }
    if (this.form.get('litigation').value) {
      this.isLitigationError();
      return;
    }

    this.formStateService.checkDirtyForm(this.form, () => {
      const confirmationResult$ = this.customerCreditNoteService
        .canCreateFromReturnNote(this.entity.id)
        .pipe(
          concatMap(canCreate => {
            if (canCreate) {
              return this.confirmationService.warn(
                'Doc::CustomerCreditNote:FromReturnNote',
                '::AreYouSure',
              );
            }
            return of(null);
          }),
        );

      confirmationResult$.subscribe(result => {
        if (result == Confirmation.Status.confirm) {
          const canvaRef = this.offcanvasService.open(
            CustomerCreditNoteCreationDialogComponent,
            null,
            {
              position: 'end',
              panelClass: 'w-100 w-sm-75 w-3xl-50 ',
            },
          );
          let customerCreditNoteCreationDialog = canvaRef.componentInstance;

          customerCreditNoteCreationDialog.returnNoteId = this.entity.id;

          (
            canvaRef.componentInstance as CustomerCreditNoteCreationDialogComponent
          ).customerCreditNoteCreated.subscribe((customerCreditNote: CustomerCreditNoteDto) => {
            this.router.navigate(['../../customer-credit-notes', customerCreditNote.id], {
              relativeTo: this.route,
            });
          });
        }
      });
    });
  }

  getEntityCallback(): void {
    super.getEntityCallback();
    super.loadThirdParties(this.entity.isClosed);

    this.docLineGrid?.lineGrid?.grid?.grid?.api?.redrawRows();
  }

  protected createEntity(defaultValue) {
    defaultValue.isValidated = true;

    return defaultValue;
  }

  private addPayment() {
    if (this.form.get('isBlocked').value) {
      this.isBlockedError();
      return;
    }
    if (this.form.get('litigation').value) {
      this.isLitigationError();
      return;
    }
    this.formStateService.checkDirtyForm(this.form, () => {
      let canvaRef = this.offcanvasService.open(CustomerInvoicePaymentsComponent, null, {
        position: 'end',
        panelClass: 'w-100 w-sm-75',
      });
      (canvaRef.componentInstance as CustomerInvoicePaymentsComponent).invoiceId = this.entity.id;
      (canvaRef.componentInstance as CustomerInvoicePaymentsComponent).service.set(
        this.customerReturnNoteService,
      );
      canvaRef.dismissed.subscribe(() => {
        this.getEntity(this.entity.id);
      });
    });
  }

  protected override onThirdPartyChanged = (thirdPartyId: string): void => {
    super.onThirdPartyChanged(thirdPartyId);
    this.thirdPartyId$.next(thirdPartyId);
  };

  protected override getFieldMappings(): FieldMapping[] {
    return [
      ...super.getFieldMappings(),
      { formField: 'representativeCommision', customerField: 'representativeCommission' },
      { formField: 'currencyId', customerField: 'currencyId' },
    ];
  }

  protected templateImportedCallback(updatedLines: BaseDocLineDto[]) {
    this.docLineGrid.lineGrid.grid.grid.api.applyTransaction({
      add: updatedLines,
    });
  }

  protected override applyThirdPartyConfiguration(thirdParty: ThirdPartyDto) {
    const documentConfiguration = this.currentCompanyStore.currentCompany()?.documentsConfiguration;
    if (documentConfiguration?.paymentTermId != null) {
      this.form.get('paymentTermId')?.setValue(documentConfiguration.paymentTermId);
    }
    if (!documentConfiguration.useCustomerFinancialDiscountInReturnWorkflow) {
      this.form.get('financialDiscountDays')?.setValue(null);
      this.form.get('financialDiscount')?.setValue(null);
    }
  }

  protected override async afterGetEntity(response: CustomerReturnNoteDto): Promise<void> {
    await super.afterGetEntity(response);
    this.subscribeToFormValueChanges();

    this.dayBooks$.subscribe(dayBooks => {
      this.dayBook = dayBooks.find(x => x.id == this.entity?.dayBookId);
      this.canAddPayment.set(
        this.dayBook?.paymentDayBookId != null && this.dayBook?.refundDayBookId != null,
      );

      if (this.canAddPayment()) {
        this.customerReturnNoteService
          .getRemainingAmount(this.entity.id)
          .subscribeOnce(remaining => {
            this.remainingAmount$ = of(remaining);
          });
      }
    });
  }

  protected override afterSave(): void {
    super.afterSave();
    if (this.validateInProgress) {
      this.docService.validateStock(this.entity.id).subscribeOnce(() => {
        this.refreshEntity();
        this.validateInProgress = false;
        this.toastService.success('Doc::CustomerQuote:IsValidated');
        if (this.isValidated() && this.canAddPayment && this.dayBook.isImmediatePayment) {
          this.addPayment();
        }
      });
    } else {
      this.subscribeToFormValueChanges();
    }
  }

  protected validateStock() {
    this.validateInProgress = true;
    this.save();
  }

  public override canDeactivate = async () => {
    this.canDeactivateTitle = this.stockValidationSharedLogicService.getCanDeactivateTitle();
    return !this.form.dirty;
  };
}
